/* eslint-disable radix */
import { useEffect, useRef, useState } from 'react';
import { Flex, Typography, Alert } from 'antd';
import { useTranslation } from 'react-i18next';

const PDFViewer = ({ highlightData }: { highlightData: any }) => {
  const { i18n, t } = useTranslation();
  const locale = i18n.language;
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  useEffect(() => {
    const iframe = iframeRef.current;
    setPdfError(null);

    const handleLoad = () => {
      const win = iframe?.contentWindow as any;
      const doc = iframe?.contentDocument;

      if (!win || !doc) return;

      const setupErrorHandling = (viewerApp: any) => {
        if (!viewerApp?.eventBus) return;

        const handleError = (error: any) => {
          let errorMessage = t('home.pdfViewer.error.generic');

          if (error?.message) {
            errorMessage = error.message;
          } else if (error?.toString) {
            errorMessage = error.toString();
          } else if (typeof error === 'string') {
            errorMessage = error;
          }

          if (
            errorMessage.includes('Invalid PDF') ||
            errorMessage.includes('Invalid PDF structure') ||
            errorMessage.includes('corrupted')
          ) {
            errorMessage = t('home.pdfViewer.error.invalidStructure');
          }

          setPdfError(errorMessage);
        };

        const handleDocumentError = (event: any) => {
          const errorInfo = event?.detail || event;
          let errorMessage = t('home.pdfViewer.error.generic');

          if (errorInfo?.message) {
            errorMessage = errorInfo.message;
          } else if (typeof errorInfo === 'string') {
            errorMessage = errorInfo;
          }

          if (
            errorMessage.includes('Invalid PDF') ||
            errorMessage.includes('Invalid PDF structure') ||
            errorMessage.includes('corrupted') ||
            errorMessage.includes('invalid-file-error')
          ) {
            errorMessage = t('home.pdfViewer.error.invalidStructure');
          }

          setPdfError(errorMessage);
        };

        viewerApp.eventBus.on('documenterror', handleDocumentError);
        viewerApp.eventBus.on('error', handleError);
        viewerApp.eventBus.on('fileloadingerror', handleError);

        if (viewerApp.pdfLoadingTask) {
          viewerApp.pdfLoadingTask.promise.catch((reason: any) => {
            let errorMessage = t('home.pdfViewer.error.generic');

            if (reason?.message) {
              errorMessage = reason.message;
            } else if (reason?.toString) {
              errorMessage = reason.toString();
            } else if (typeof reason === 'string') {
              errorMessage = reason;
            }

            if (
              errorMessage.includes('Invalid PDF') ||
              errorMessage.includes('Invalid PDF structure') ||
              errorMessage.includes('corrupted') ||
              reason?.name === 'InvalidPDFException'
            ) {
              errorMessage = t('home.pdfViewer.error.invalidStructure');
            }

            setPdfError(errorMessage);
          });
        }

        const originalOpen = viewerApp.open;
        if (originalOpen) {
          viewerApp.open = async function (...args: any[]) {
            try {
              const result = await originalOpen.apply(this, args);
              return result;
            } catch (error: any) {
              let errorMessage = t('home.pdfViewer.error.generic');

              if (error?.message) {
                errorMessage = error.message;
              } else if (error?.toString) {
                errorMessage = error.toString();
              }

              if (
                errorMessage.includes('Invalid PDF') ||
                errorMessage.includes('Invalid PDF structure') ||
                errorMessage.includes('corrupted') ||
                error?.name === 'InvalidPDFException'
              ) {
                errorMessage = t('home.pdfViewer.error.invalidStructure');
              }

              setPdfError(errorMessage);
              throw error;
            }
          };
        }
      };

      const onPagesLoaded = () => {
        const viewerApp = win?.PDFViewerApplication;
        const pdfViewer = viewerApp?.pdfViewer;
        const pageNumber = parseInt(highlightData.page);
        setTimeout(async () => {
          try {
            viewerApp.pdfViewer.scrollPageIntoView({
              pageNumber: parseInt(highlightData.page),
            });
            const pageView = pdfViewer?.getPageView(pageNumber - 1);
            const viewport = pageView?.viewport;
            const pdfPage = await pageView?.pdfPage;

            if (!pdfPage) return;

            const textContent = await pdfPage.getTextContent();
            const items = textContent.items;

            const searchText = highlightData.ref.replace(
              /^[\s\S]*?(Reference|参照元)[\s*\:\：]*\**\s*/i,
              '',
            );

            let fullText = '';
            const charMap: { index: number; itemIndex: number }[] = [];

            items.forEach((item: any, i: number) => {
              const str = item.hasEOL ? ' ' : item.str;
              for (let j = 0; j < str.length; j++) {
                charMap.push({ index: fullText.length + j, itemIndex: i });
              }
              fullText += item.hasEOL ? ' ' : str;
            });

            const startChar = fullText.indexOf(searchText);
            if (startChar === -1) {
              return;
            }

            const endChar = startChar + searchText.length;
            const involvedItems = new Set<number>();
            for (let i = startChar; i < endChar; i++) {
              if (charMap[i]) {
                involvedItems.add(charMap[i].itemIndex);
              }
            }

            const boxes: any[] = [];
            [...involvedItems]
              .sort((a, b) => a - b)
              .forEach(i => {
                const item = items[i] as any;
                const { transform, width, height } = item;

                boxes.push({
                  x: transform[4],
                  y:
                    viewport.height / viewport.scale -
                    transform[5] -
                    transform[3],
                  width,
                  height: height || 10,
                });
              });

            if (boxes.length > 0) {
              const data = {
                action: 'showHighlight',
                externalId: `highlight-${Date.now()}`,
                pageIndex: pageNumber - 1,
                boxes,
                text: searchText,
                color: 'rgba(255, 255, 0, 0.5)',
                customData: { type: 'highlight' },
              };
              win.postMessage(data, '*');
            }
          } catch (error: any) {
            const errorMessage =
              error?.message ||
              error?.toString() ||
              t('home.pdfViewer.error.generic');
            setPdfError(errorMessage);
          }
        }, 500);
      };

      const setupIframeErrorHandling = () => {
        const handleIframeUnhandledRejection = (
          event: PromiseRejectionEvent,
        ) => {
          const error = event.reason;
          const errorStack = error?.stack || '';
          const errorMessage = error?.message || error?.toString() || '';

          const isPdfError =
            error?.name === 'InvalidPDFException' ||
            error?.name === 'MissingPDFException' ||
            error?.name === 'UnexpectedResponseException' ||
            errorStack.includes('pdf.mjs') ||
            errorStack.includes('pdfViewer') ||
            errorMessage.includes('Invalid PDF') ||
            errorMessage.includes('PDF') ||
            errorMessage.includes('pdf');

          if (isPdfError) {
            let displayMessage = t('home.pdfViewer.error.generic');

            if (errorMessage) {
              displayMessage = errorMessage;
            }

            if (
              displayMessage.includes('Invalid PDF') ||
              displayMessage.includes('Invalid PDF structure') ||
              displayMessage.includes('corrupted') ||
              error?.name === 'InvalidPDFException'
            ) {
              displayMessage = t('home.pdfViewer.error.invalidStructure');
            }

            setPdfError(displayMessage);
            event.preventDefault();
          }
        };

        win.addEventListener(
          'unhandledrejection',
          handleIframeUnhandledRejection,
        );
      };

      setupIframeErrorHandling();

      const viewerApp = win.PDFViewerApplication;
      if (viewerApp?.eventBus) {
        setupErrorHandling(viewerApp);
        viewerApp.eventBus.on('pagesloaded', onPagesLoaded);
      } else {
        const interval = setInterval(() => {
          const viewerApp = win?.PDFViewerApplication;
          if (viewerApp?.eventBus) {
            clearInterval(interval);
            setupErrorHandling(viewerApp);
            viewerApp.eventBus.on('pagesloaded', onPagesLoaded);
          }
        }, 500);
      }
    };

    const handleError = () => {
      setPdfError(t('home.pdfViewer.error.loadFailed'));
    };

    const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
      const error = event.reason;
      const errorStack = error?.stack || '';
      const errorMessage = error?.message || error?.toString() || '';

      const isPdfError =
        error?.name === 'InvalidPDFException' ||
        error?.name === 'MissingPDFException' ||
        error?.name === 'UnexpectedResponseException' ||
        errorStack.includes('pdf.mjs') ||
        errorStack.includes('pdfViewer') ||
        errorMessage.includes('Invalid PDF') ||
        errorMessage.includes('PDF') ||
        errorMessage.includes('pdf');

      if (isPdfError) {
        let displayMessage = t('home.pdfViewer.error.generic');

        if (errorMessage) {
          displayMessage = errorMessage;
        }

        if (
          displayMessage.includes('Invalid PDF') ||
          displayMessage.includes('Invalid PDF structure') ||
          displayMessage.includes('corrupted') ||
          error?.name === 'InvalidPDFException'
        ) {
          displayMessage = t('home.pdfViewer.error.invalidStructure');
        }

        setPdfError(displayMessage);
        event.preventDefault();
      }
    };

    if (iframe) {
      iframe.addEventListener('load', handleLoad);
      iframe.addEventListener('error', handleError);
    }

    window.addEventListener('unhandledrejection', handleUnhandledRejection);

    return () => {
      iframe?.removeEventListener('load', handleLoad);
      iframe?.removeEventListener('error', handleError);
      window.removeEventListener(
        'unhandledrejection',
        handleUnhandledRejection,
      );
    };
  }, [highlightData, t]);

  return (
    <Flex vertical gap={8} style={{ height: 'calc(100vh - 30px)' }}>
      <div>
        <Typography.Text className="text-[10px] font-normal">
          {highlightData?.name}
        </Typography.Text>
      </div>
      {pdfError ? (
        <Alert
          message={t('home.pdfViewer.error.title')}
          description={pdfError}
          type="error"
          showIcon
          style={{ margin: '20px' }}
        />
      ) : (
        <iframe
          ref={iframeRef}
          src={`/pdfViewer/web/viewer.html?file=${encodeURIComponent(highlightData?.path)}&force=true&skipUnsavedConfirm=true&locale=${locale}`}
          width="100%"
          height="100%"
          className="relative h-screen w-full"
        />
      )}
      <div
        id="highlight-layer"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          pointerEvents: 'none',
          width: '100%',
          height: '100%',
        }}
      ></div>
    </Flex>
  );
};

export default PDFViewer;
